import { UpdateUserProfile, UserProfile } from '@/models/UserProfile';
import { apiClient, isApiError } from '@/services/api';
import { logger } from '@/utils/logger/loggerInstance';

export const getUserProfile = async () => {
  const url = `/users/profile`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<UserProfile>(url);

  return response.data;
};

export interface PublicUserProfile {
  cognitoUserId: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  calLink?: string;
  lastActiveAt?: string;
  // Present when requester and target share a client group
  email?: string;
  phoneNumber?: number;
}

export const getPublicUserProfile = async (userId: string): Promise<PublicUserProfile> => {
  const url = `/users/${encodeURIComponent(userId)}/profile`;

  logger.debug(`GET ${url}`);
  const response = await apiClient.get<PublicUserProfile>(url);

  return response.data;
};

export const updateUserProfile = async (requestBody: UpdateUserProfile) => {
  const url = '/users/profile';

  try {
    logger.debug(`PUT ${url}`);
    const response = await apiClient.put<UserProfile>(url, requestBody);
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'An error occurred');
    }
    throw error;
  }
};

export const requestAccountDeletion = async () => {
  const url = '/users/request-account-deletion';

  try {
    logger.debug(`POST ${url}`);
    const response = await apiClient.post<void>(url);
    return response.data;
  } catch (error) {
    if (isApiError<{ message?: string }>(error)) {
      throw new Error(error.response?.data.message ?? 'An error occurred');
    }
    throw error;
  }
};

export interface AvatarPresignedUrlResponse {
  presignedUrl: string;
  publicUrl: string;
  key: string;
}

export const getAvatarUploadUrl = async (
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp',
): Promise<AvatarPresignedUrlResponse> => {
  const url = '/users/avatar/presigned-url';
  logger.debug(`POST ${url}`);
  const response = await apiClient.post<AvatarPresignedUrlResponse>(url, { mimeType });
  return response.data;
};

export interface SearchUsersParams {
  role: 'Buyer' | 'Advisor' | 'Admin';
  nextToken?: string;
}

export interface SearchUsersResponse {
  users: Array<{
    userId: string;
    email?: string;
    name?: string;
  }>;
  nextToken?: string;
}

export type UserPresenceMap = Record<string, string | null>;

export const getUserPresence = async (userIds: string[]): Promise<UserPresenceMap> => {
  if (userIds.length === 0) return {};
  const url = `/users/presence?userIds=${userIds.map(encodeURIComponent).join(',')}`;
  logger.debug(`GET ${url}`);
  const response = await apiClient.get<UserPresenceMap>(url);
  return response.data;
};

export const searchUsers = async (params: SearchUsersParams): Promise<SearchUsersResponse> => {
  const url = '/users/search';
  const queryParams = new URLSearchParams({ role: params.role });
  if (params.nextToken) {
    queryParams.append('nextToken', params.nextToken);
  }

  logger.debug(`GET ${url}?${queryParams.toString()}`);
  const response = await apiClient.get<SearchUsersResponse>(`${url}?${queryParams.toString()}`);
  return response.data;
};
